<template>
    <KContentBlock
        v-if="article"
        v-editable="blok"
        class="article-highlight"
        as="div"
        container
        stack="overlay"
        align="stretch"
        variant="subtle"
        padding="none"
        title-as="h3"
        :text="description"
    >
        <template #title>
            <span v-html="title" />
        </template>

        <template #aside>
            <KFrame scrim>
                <NuxtImg
                    :src="image.filename"
                    :alt="image.alt || ''"
                    :width="image.width"
                    :height="image.height"
                    sizes="xs:100vw sm:100vw md:50vw"
                    format="auto"
                    loading="lazy"
                />
            </KFrame>
        </template>

        <template
            v-if="link"
            #actions
        >
            <KButton
                as="a"
                :href="link"
                :label="blok.button_text || 'Read Article'"
            >
                <template #icon-end>
                    <Icon name="tabler:arrow-right" />
                </template>
            </KButton>
        </template>
    </KContentBlock>
</template>

<script setup>
import { KButton, KContentBlock, KFrame } from '@vemedia/karibu'
import { computed } from 'vue'
import { useAsyncData } from '#imports'
import renderHighlightedText from '~/helpers/renderHighlightedText'
import storyblokImageSize from '~/helpers/storyblokImageSize'

const { blok } = defineProps({
    blok: {
        type: Object,
        default: () => ({}),
    },
})

const { data: article } = useAsyncData(`article-highlight-${blok.article}`, async () => $fetch(`/api/page?uuid=${blok.article}`))

const title = computed(() => {
    if (!article.value)
        return ''
    return blok.title || renderHighlightedText(article.value.content.title)
})

const description = computed(() => {
    if (!article.value)
        return ''
    return blok.text || article.value.content.subtitle
})

const link = computed(() => {
    if (!article.value)
        return ''
    return `/${article.value.full_slug}`
})

// The KFrame wrapping this image has no explicit ratio (`aspect-ratio: auto`),
// so the intrinsic width/height on the <img> are what reserves layout before
// load. @nuxt/image also feeds them into the Cloudflare transform (no `fit`,
// so it only downscales) — without them the aside used to ship the unresized
// original. Dims ride the same computed because the source flips between the
// blok's own image and the linked article's once the fetch resolves.
const image = computed(() => {
    const source = article.value ? blok.image || article.value?.content?.image : null
    const dims = storyblokImageSize(source?.filename)

    return {
        filename: source?.filename || '//',
        alt: source?.alt || '',
        width: dims?.width,
        height: dims?.height,
    }
})
</script>

<style lang="scss">
@use 'article-highlight';
</style>
